// Chart fallback palettes — the single owner of "pick a colour by position".
//
// These are CHART DATA, not design tokens: a series colour has to be a concrete value a
// chart library can consume, and the mock fixtures say the same of their fund `color`
// fields. They are the documented exception to Hard Rule #7, which is why they live here
// (one place, reviewable) rather than being sprinkled through components.
//
// WHY THREE PALETTES AND NOT ONE. A review flagged these as four copies of one sequence.
// Two of them genuinely were — `FUND_PALETTE` (manage-account.adapters) and
// `FALLBACK_COLORS` (portfolio.adapters) were byte-identical, and are now the single
// `FUND_BLUE_RAMP` below. The other two are deliberately different and collapsing them
// would change what renders:
//
//   • FUND_BLUE_RAMP  — a monochromatic navy→sky ramp. Right for a fund LIST, where the
//                       colours order the rows and shouldn't compete with each other.
//   • CATEGORICAL     — multi-hue, for series that must be told apart at a glance rather
//                       than ordered; a blue ramp renders two neighbouring segments as one
//                       indistinct block. NOTE it does not currently reach the dashboard's
//                       fund segments: `holdingColor` prefers the portfolio slice's
//                       per-ticker colour for cross-screen identity, and those come from
//                       FUND_BLUE_RAMP. It applies to models and unknown tickers only.
//                       See the note on `holdingColor` in `utils/holdings.js`.
//   • FUND_MIX        — five colours for the fund-detail modal's mix chart, which never
//                       shows more than five slices.
//
// So the duplication is gone, and no screen's colours moved. Any future rebrand edits
// this file only.
//
// Every palette starts at the deepest navy so the first (largest) series reads as the
// anchor on all three surfaces.

/** Monochromatic navy → sky. Fund lists / ordered series. */
export const FUND_BLUE_RAMP = [
  '#0A1A3A', '#10295E', '#1B4283', '#0077CC',
  '#3593D6', '#5BA8E0', '#7BB9E5', '#8EC0EA',
];

/** Multi-hue. Adjacent-segment distinguishability (allocation bars, legends). */
export const CATEGORICAL = [
  '#0A1A3A', '#0077CC', '#552299', '#119944',
  '#FF6600', '#3593D6', '#8EC0EA', '#FFBB11',
];

/** Five slices max — the fund-detail modal's mix chart. */
export const FUND_MIX = ['#0A1A3A', '#0077CC', '#5BA8E0', '#8EC0EA', '#119944'];

/**
 * Colour at `index`, wrapping. Saves every caller repeating `palette[i % palette.length]`
 * and makes the wrap behaviour one decision instead of four.
 * @param {string[]} palette one of the exports above
 * @param {number} index series position
 */
export const colorAt = (palette, index) => palette[index % palette.length];

// ── Magnitude ramp (hero "Balance by source", FM-2116) ─────────────────────────
// Colour encodes % MAGNITUDE, not source identity: largest source = deepest navy,
// smallest = palest. Continuous, so it can't come from a fixed palette above —
// mirrors the design's blueForPct (dashboard-redesign.jsx:12-24), same rule as the
// Holdings visualization.
const SRC_BLUE_MAX_L = 88;
const SRC_BLUE_MIN_L = 22;
const srcBlueL = (pct, maxPct) => {
  const t = Math.sqrt(Math.max(0, Math.min(1, pct / (maxPct || 1))));
  return SRC_BLUE_MAX_L - t * (SRC_BLUE_MAX_L - SRC_BLUE_MIN_L);
};

/**
 * hsl() blue whose lightness encodes `pct` relative to the largest segment.
 * @param {number} pct segment share
 * @param {number} maxPct the largest segment's share
 */
export const blueForPct = (pct, maxPct) => `hsl(210 78% ${srcBlueL(pct, maxPct).toFixed(1)}%)`;

/** True when the ramp colour is light enough to need dark foreground content. */
export const isPaleBlue = (pct, maxPct) => srcBlueL(pct, maxPct) > 60;

export default FUND_BLUE_RAMP;
